import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  #pointerenterHandler = null;
  timer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();

    this.refs.dialog.addEventListener('click', this.#handleDialogClick, true);

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog.removeEventListener('click', this.#handleDialogClick, true);

    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);

      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else if (dialogStartY < CONTAINER_GAP) {
        verticalOffset = CONTAINER_GAP - dialogStartY;
        y = 'bottom';
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  handleHotspotClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.showDialog();
  };

  #handleDialogClick = (event) => {

  if (event.target.closest('.quick-add__button')) {
    return;
  }

  const url = this.dataset.productUrl;

  if (!url) return;

  event.preventDefault();
  event.stopPropagation();

  window.location.href = url;
};

  showDialog = async () => {
    const { dialog } = this.refs;

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    dialog.dataset.showing = 'true';
    dialog.show();

    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);

    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
  };

  closeDialog = async () => {
    const { dialog, trigger } = this.refs;

    if (!dialog.open) return;

    dialog.dataset.closing = 'true';
    dialog.close();

    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);

    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);

    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  lightDismissMouse = (event) => {
    const { dialog, trigger } = this.refs;

    if (
      event.target instanceof Element &&
      (trigger.contains(event.target) || dialog.contains(event.target))
    ) {
      return;
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;

    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);
console.log('NY HOTSPOT JS LADDAD');